import { observer } from 'mobx-react-lite'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import AttachmentIcon from '@mui/icons-material/Attachment'
import CloseOutlined from '@mui/icons-material/CloseOutlined'
import { themeConfig } from '@gap/shared/theme'
import { pluralise } from '@gap/utils/language-utils'
import { ProposalDto } from '@gap/backend/app-services/proposal/models/ProposalDto'
import useStore from 'reusable/mobx-store-utils'
import ProposalAttachmentsPanel from './ProposalAttachmentsPanel'

interface ProposalAttachmentsTriggerProps {
   proposal: ProposalDto
   /** When true, render even at md+ and use inherit colors (for use outside the AppBar e.g. in documents). */
   inline?: boolean
}

export default observer(function ProposalAttachmentsTrigger({ proposal, inline }: ProposalAttachmentsTriggerProps) {
   const theme = useTheme()
   const isSm = useMediaQuery(theme.breakpoints.up('sm'))
   const isMd = useMediaQuery(theme.breakpoints.up('md'))

   const proposalAttachmentsTriggerStore = useStore((sp) => {
      return {
         open: false,
         openDrawer: () => {
            proposalAttachmentsTriggerStore.open = true
         },
         closeDrawer: () => {
            proposalAttachmentsTriggerStore.open = false
         },
         get count() {
            return sp.proposal.step5?.attachments?.length ?? 0
         },
         get label() {
            return `${proposalAttachmentsTriggerStore.count} ${pluralise(proposalAttachmentsTriggerStore.count, 'Attachment')}`
         },
      }
   }, { proposal })

   if (proposalAttachmentsTriggerStore.count === 0) return null
   if (!inline && isMd) return null

   return <>
      <Button
         variant="text"
         startIcon={<AttachmentIcon sx={{ transform: 'rotate(270deg)', color: inline ? 'inherit' : 'white' }} />}
         onClick={proposalAttachmentsTriggerStore.openDrawer}
         sx={{ color: inline ? 'inherit' : 'white', textTransform: 'none' }}
      >
         {proposalAttachmentsTriggerStore.label}
      </Button>
      <Drawer
         anchor={isSm ? 'right' : 'bottom'}
         open={proposalAttachmentsTriggerStore.open}
         onClose={proposalAttachmentsTriggerStore.closeDrawer}
         sx={{ zIndex: theme.zIndex.modal + 1 }}
         PaperProps={{
            sx: {
               width: { xs: '100%', sm: 410 },
               maxHeight: { xs: '85vh', sm: '100%' },
               overflowY: 'auto',
               backgroundColor: themeConfig.light.background.alt,
            },
         }}
      >
         <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
            <IconButton onClick={proposalAttachmentsTriggerStore.closeDrawer} aria-label="Close attachments">
               <CloseOutlined />
            </IconButton>
         </Box>
         <ProposalAttachmentsPanel proposal={proposal} />
      </Drawer>
   </>
})
